import { useEffect, useRef, useState } from "react"
import { Navigate } from "react-router-dom"
import { canPerform } from "../../auth/actions"
import { isPlatformUser } from "../../auth/platform-roles"
import { useAuth } from "../../contexts/AuthContext"
import { useTenant } from "../../contexts/TenantContext"
import { customerOverviewPath } from "../../libs/customer-paths"

/**
 * RootRedirect handles the default "/" route by redirecting to:
 * - Single customer: that customer's overview (via customerOverviewPath)
 * - Multiple customers or admin: /customer-picker
 */
export function RootRedirect() {
  const { isAuthenticated, isLoading, accessibleCustomers, fetchAccessibleCustomers, user } =
    useAuth()
  const { tenant } = useTenant()
  const [hasFetched, setHasFetched] = useState(false)
  // Track the tenant we last fetched for so a tenant switch on this mounted
  // component triggers a refetch instead of reusing the stale prior list.
  const fetchedTenantRef = useRef<string | null>(null)

  useEffect(() => {
    if (!isAuthenticated || !tenant?.id) return
    if (fetchedTenantRef.current === tenant.id) return

    fetchedTenantRef.current = tenant.id
    setHasFetched(false)
    fetchAccessibleCustomers(tenant.id)
      .catch((err) => {
        console.error("Failed to fetch customers:", err)
      })
      .finally(() => {
        setHasFetched(true)
      })
  }, [isAuthenticated, tenant?.id, fetchAccessibleCustomers])

  if (isLoading || (isAuthenticated && tenant && !hasFetched)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-content-2">Loading...</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (isPlatformUser(user?.platformRole) && !canPerform(user?.platformRole, "app.customers")) {
    return <Navigate to="/admin" replace />
  }

  const lastCustomerId = localStorage.getItem("last_selected_customer")
  if (lastCustomerId && accessibleCustomers.some((c) => c.customer?.id === lastCustomerId)) {
    return <Navigate to={customerOverviewPath(lastCustomerId)} replace />
  }

  if (accessibleCustomers.length === 1) {
    const customerId = accessibleCustomers[0].customer?.id
    if (customerId) {
      localStorage.setItem("last_selected_customer", customerId)
      return <Navigate to={customerOverviewPath(customerId)} replace />
    }
  }

  if (accessibleCustomers.length > 1 || user?.isGlobalAdmin) {
    return <Navigate to="/customer-picker" replace />
  }

  return <Navigate to="/customer-picker" replace />
}
